/**
 * Where a review's price goes, and a coach's paid reviews as money. Pure: the
 * portal works it out from the coach's own submissions and the config, so the
 * figures are estimates until the payout ledger records the real ones.
 */
import type { CoachingConfig } from "./config";
import { OPEN_STATUSES, type SubmissionPayment, type SubmissionStatus } from "./types";

export type FeeConfig = Pick<CoachingConfig, "storeFeeBps" | "platformFeeBps">;

/** One price split three ways: the store's fee first, Swana's share of what is left, the club gets the rest. */
export interface MoneySplit {
    grossPence: number;
    storePence: number;
    swanaPence: number;
    clubPence: number;
}

const ZERO: MoneySplit = { grossPence: 0, storePence: 0, swanaPence: 0, clubPence: 0 };

export function splitOf(grossPence: number, fees: FeeConfig): MoneySplit {
    const storePence = Math.round((grossPence * fees.storeFeeBps) / 10_000);
    const swanaPence = Math.round(((grossPence - storePence) * fees.platformFeeBps) / 10_000);
    return { grossPence, storePence, swanaPence, clubPence: grossPence - storePence - swanaPence };
}

const add = (a: MoneySplit, b: MoneySplit): MoneySplit => ({
    grossPence: a.grossPence + b.grossPence,
    storePence: a.storePence + b.storePence,
    swanaPence: a.swanaPence + b.swanaPence,
    clubPence: a.clubPence + b.clubPence,
});

/** "15%" or "12.5%", for a fee in basis points. */
export function percentText(bps: number): string {
    return `${Math.round(bps) / 100}%`;
}

/** One submission as the money view needs it, times in epoch ms. */
export interface MoneyRow {
    status: SubmissionStatus;
    payment: SubmissionPayment | null;
    returnedAt: number | null;
}

export interface MonthMoney {
    /** "2026-11", the month the club's share falls due, in UK time. */
    month: string;
    reviews: number;
    split: MoneySplit;
}

export interface CoachMoney {
    /** Paid reviews that were not refunded, and their money. */
    kept: number;
    collected: MoneySplit;
    /** Paid and not yet returned: nothing is owed until the review goes back. */
    inProgress: { count: number; split: MoneySplit };
    /** Returned but inside the hold, and when the next one is released. */
    held: { count: number; split: MoneySplit; nextReleaseAt: number | null };
    /** Past the hold, by the month each fell due, newest first. */
    months: MonthMoney[];
    /** Refunded, or closed unreviewed with the refund still to be issued. */
    refunds: { count: number; pending: number; grossPence: number };
    /** Store test purchases among the paid reviews, so a real-looking total can be told apart. */
    testPurchases: number;
}

const DAY_MS = 86_400_000;

const UK_MONTH = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "2-digit" });

/** "2026-11" for the UK month an instant falls in. */
export function ukMonthOf(ms: number): string {
    const parts = UK_MONTH.formatToParts(new Date(ms));
    const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    return `${part("year")}-${part("month")}`;
}

/** The instant the UK month containing `ms` began: midnight on the 1st, in BST or GMT as it was then. */
export function ukMonthStart(ms: number): number {
    const [y, m] = ukMonthOf(ms).split("-").map(Number);
    const utcMidnight = Date.UTC(y, m - 1, 1);
    // In BST the UK month starts an hour before UTC midnight; 23:00 UTC the day before is then already inside it.
    return ukMonthOf(utcMidnight - 3_600_000) === ukMonthOf(utcMidnight) ? utcMidnight - 3_600_000 : utcMidnight;
}

type Placed =
    | { kind: "refund"; pending: boolean }
    | { kind: "in_progress" }
    | { kind: "held"; releaseAt: number }
    | { kind: "due"; month: string };

function place(row: MoneyRow, payment: SubmissionPayment, now: number, holdDays: number): Placed {
    if (payment.status !== "paid") return { kind: "refund", pending: payment.status === "refund_due" };
    if (row.status === "returned" && row.returnedAt !== null) {
        const releaseAt = row.returnedAt + holdDays * DAY_MS;
        return releaseAt > now ? { kind: "held", releaseAt } : { kind: "due", month: ukMonthOf(releaseAt) };
    }
    if (OPEN_STATUSES.includes(row.status)) return { kind: "in_progress" };
    // Closed without a review but not yet marked refund_due: the refund is owed all the same.
    return { kind: "refund", pending: true };
}

/**
 * A coach's paid reviews as money: what players paid, what is in progress,
 * held and due to the club by month, and what was refunded. Rows without a
 * payment (sent before payments, or free) are left out.
 */
export function coachMoney(rows: readonly MoneyRow[], now: number, cfg: FeeConfig & Pick<CoachingConfig, "payoutHoldDays">): CoachMoney {
    const out: CoachMoney = {
        kept: 0,
        collected: ZERO,
        inProgress: { count: 0, split: ZERO },
        held: { count: 0, split: ZERO, nextReleaseAt: null },
        months: [],
        refunds: { count: 0, pending: 0, grossPence: 0 },
        testPurchases: 0,
    };
    const months = new Map<string, MonthMoney>();
    for (const row of rows) {
        const payment = row.payment;
        if (!payment) continue;
        if (payment.sandbox) out.testPurchases += 1;
        const at = place(row, payment, now, cfg.payoutHoldDays);
        if (at.kind === "refund") {
            out.refunds.count += 1;
            if (at.pending) out.refunds.pending += 1;
            out.refunds.grossPence += payment.pricePence;
            continue;
        }
        const split = splitOf(payment.pricePence, cfg);
        out.kept += 1;
        out.collected = add(out.collected, split);
        if (at.kind === "in_progress") {
            out.inProgress = { count: out.inProgress.count + 1, split: add(out.inProgress.split, split) };
        } else if (at.kind === "held") {
            const next = out.held.nextReleaseAt;
            out.held = { count: out.held.count + 1, split: add(out.held.split, split), nextReleaseAt: next === null ? at.releaseAt : Math.min(next, at.releaseAt) };
        } else {
            const m = months.get(at.month) ?? { month: at.month, reviews: 0, split: ZERO };
            months.set(at.month, { month: at.month, reviews: m.reviews + 1, split: add(m.split, split) });
        }
    }
    out.months = [...months.values()].sort((a, b) => b.month.localeCompare(a.month));
    return out;
}
